import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response';
import { environment } from 'src/environments/environment';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { getTenantId, getDepartmentId } from 'src/app/services/store-snapshot.service';
import { IdName } from 'src/app/shared/models/id-name';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { ALL_DEPS_TOKEN } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { IDelegateNonConformanceRequest } from '../models/delegate-non-conformance-request';
import { IDelegateNonConformanceModel } from '../models/delegate-non-conformance.model';
import { IListNonConformanceModel } from '../models/list-non-conformance.model';
import { INonConformanceModel, INonConformanceRequest } from '../models/non-conformance.model';
import { IPrintNonConformanceModel } from '../models/print-non-conformance.model';
import { NonConformanceStatus } from '../enums/non-conformance-status.enum';
import { IdComment } from '../models/id-comment.model';
import { INonConformanceVersion } from '../models/non-conformance-version.model';
import { FormDataService } from '../../shared/attachments/service/form-data.service';
import { ISignNonConformanceModel } from '../models/sign-non-conformance.model';
import { INonConformanceStateCountResponse } from '../models/non-conformance-state-count-response.model';

const COLUMN_TO_KEY_MAP: Record<string, string> = {
  type: 'Type.Name',
  severity: 'Severity.Name',
  responsibleForFollowUp: 'PositionsResponsibleForFollowUp.Name',
  responsibleForClosing: 'PositionsResponsibleForClosing.Name',
  department: 'Department.Name',
};

@Injectable({
  providedIn: 'root',
})
export class NonConformanceApiService {
  private readonly headers = { 'content-type': 'application/json' };
  private get baseUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/NonConformance`;
  }
  private get allDepsUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/NonConformance`;
  }

  private get nonConformancesForIOUrl(): string {
    return `${this.baseUrl}/Interconnected`;
  }

  constructor(private http: HttpClient, private store: Store) {}

  getAllNonConformancesByState(
    state: NonConformanceStatus,
    currentFilter?: MainTableFilterModel,
    allDeps?: boolean
  ): Observable<ApiResponse<IListNonConformanceModel[]>> {
    let params = new HttpParams();

    if (currentFilter) {
      params = params
        .set('Page', currentFilter.pageIndex.toString())
        .set('PageSize', currentFilter.pageSize.toString());
    }

    if (currentFilter?.searchTerm) {
      params = params.set('GlobalSearch', currentFilter.searchTerm);
    }

    if (currentFilter?.orderBy && currentFilter.ascDesc) {
      params = params.set(`OrderBy[${currentFilter.orderBy}]`, currentFilter.ascDesc);
    }
    let url = '';

    if (allDeps) {
      if (state === NonConformanceStatus.Deleted) {
        params = params.set('IsDeleted', 'true');
      } else {
        params = params.set('States', state);
      }
      url = this.allDepsUrl;

      params = MainTableFilteringHelperService.appendFilteringParams(
        params,
        currentFilter,
        COLUMN_TO_KEY_MAP
      );

      return this.http.get<ApiResponse<IListNonConformanceModel[]>>(url, { params });
    }

    if (state === NonConformanceStatus.Deleted) {
      params = params.set('IsDeleted', 'true');
      url = `${this.baseUrl}`;
    } else {
      params = params.set('State', state);
      url = `${this.baseUrl}/State`;
    }

    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      currentFilter,
      COLUMN_TO_KEY_MAP
    );

    return this.http.get<ApiResponse<IListNonConformanceModel[]>>(url, { params });
  }

  getNonConformanceById(id: string, ncDepartmentId?: string): Observable<INonConformanceModel> {
    let url = '';
    if (ncDepartmentId) {
      url = `${environment.apiUrl}/api/${getTenantId()}/${ncDepartmentId}/NonConformance/${id}`;
    } else {
      url = `${this.baseUrl}/${id}`;
    }
    return this.http.get<INonConformanceModel>(url);
  }

  getNonConformancesForIO(): Observable<IdName[]> {
    return this.http.get<IdName[]>(this.nonConformancesForIOUrl);
  }

  createNonConformance(nonConformance: INonConformanceRequest): Observable<INonConformanceModel> {
    const formData = FormDataService.mapToFormData(nonConformance);
    return this.http.post<INonConformanceModel>(this.baseUrl, formData);
  }

  updateNonConformance(nonConformance: INonConformanceRequest): Observable<INonConformanceModel> {
    const formData = FormDataService.mapToFormData(nonConformance);
    return this.http.put<INonConformanceModel>(this.baseUrl, formData);
  }

  deleteNonConformance(id: string): Observable<void> {
    const url = `${this.baseUrl}/${id}`;
    return this.http.delete<void>(url);
  }

  delegateNonConformance(
    delegateNonConformance: IDelegateNonConformanceRequest
  ): Observable<IDelegateNonConformanceModel> {
    const url = `${this.baseUrl}/Delegate`;
    return this.http.put<IDelegateNonConformanceModel>(url, delegateNonConformance);
  }

  closeNonConformance(request: IdComment): Observable<void> {
    const url = `${this.baseUrl}/Close`;
    return this.http.put<void>(url, request);
  }

  reopenNonConformance(request: IdComment): Observable<void> {
    const url = `${this.baseUrl}/Reopen`;
    return this.http.put<void>(url, request);
  }

  restoreNonConformance(id: string, ncDepartmentId?: string): Observable<void> {
    let url = '';
    if (ncDepartmentId) {
      url = `${
        environment.apiUrl
      }/api/${getTenantId()}/${ncDepartmentId}/NonConformance/${id}/Restore`;
    } else {
      url = `${this.baseUrl}/${id}/Restore`;
    }

    return this.http.put<void>(url, {});
  }

  completeNonConformance(request: IdComment): Observable<void> {
    const url = `${this.baseUrl}/Complete`;
    return this.http.put<void>(url, request);
  }

  rejectNonConformance(request: IdComment): Observable<void> {
    const url = `${this.baseUrl}/Reject`;
    return this.http.put<void>(url, request);
  }

  getNonConformanceLogs(id: string): Observable<INonConformanceVersion[]> {
    const url = `${this.baseUrl}/${id}/Log`;
    return this.http.get<INonConformanceVersion[]>(url);
  }

  signCompletedNonConformance(request: ISignNonConformanceModel): Observable<void> {
    const url = `${this.baseUrl}/Sign`;
    return this.http.put<void>(url, request);
  }

  downloadNonConformanceFile(printNonConformance: IPrintNonConformanceModel): Observable<Blob> {
    let url = `${this.baseUrl}/${printNonConformance.id}/Print`;
    if (
      printNonConformance.includeComments ||
      printNonConformance.includeDetails ||
      printNonConformance.listAttachments
    ) {
      url += `?IncludeDetails=${printNonConformance.includeDetails}&IncludeComments=${printNonConformance.includeComments}&IncludeAttachments=${printNonConformance.listAttachments}`;
    }
    return this.http.get(url, { responseType: 'blob' });
  }

  getNonConformanceStateCounts(): Observable<INonConformanceStateCountResponse> {
    const allDeps = getDepartmentId() === ALL_DEPS_TOKEN;
    let url = '';
    if (allDeps) {
      url = `${this.allDepsUrl}/EachStateCount`;
    } else {
      url = `${this.baseUrl}/EachStateCount`;
    }

    return this.http.get<INonConformanceStateCountResponse>(url);
  }

  printReport(
    filter?: MainTableFilterModel,
    menuId?: string,
    status?: NonConformanceStatus
  ): Observable<Blob> {
    const allDeps = getDepartmentId() === ALL_DEPS_TOKEN;
    let url = '';
    if (allDeps) {
      url = `${this.allDepsUrl}/Report`;
    } else {
      url = `${this.baseUrl}/Report`;
    }

    let params = new HttpParams();
    if (filter?.searchTerm) {
      params = params.append('GlobalSearch', filter.searchTerm);
    }
    if (filter?.orderBy && filter.ascDesc) {
      params = params.append(`OrderBy[${filter.orderBy}]`, filter.ascDesc);
    }
    if (menuId) {
      params = params.append('MenuId', menuId);
    }
    if (status === NonConformanceStatus.Deleted) {
      params = params.set('IsDeleted', 'true');
    } else if (status) {
      params = params.set('States', `${[status]}`);
    }

    if (filter) {
      params = MainTableFilteringHelperService.appendFilteringParams(
        params,
        filter,
        COLUMN_TO_KEY_MAP
      );
    }

    return this.http.get(url, { params, responseType: 'blob' });
  }
}
